import { peek } from '@art-suite/art-core-ts';
import { PhotoSetStats } from '../contexts/syncing';
import { FileSystemInterface } from '../FileSystem/FileSystemInterface';
import { pathJoin } from '../lib/pathJoin';
import { safeFsOp } from '../lib/safeFsOp';

/** Filename pattern for scaled photos: `<photoId>_<digits>.jpg`. Used to skip legacy scaled files found in the unsynced dir. */
const SCALED_NAME_RE = /^.+_\d+\.jpg$/;

const extractPhotoIdFromFilename = (filename: string): string => {
  filename = filename.split('/').pop() || filename;
  return filename.match(/^(.+?)(_\d+)?\.jpg$/)![1];
};

const isOriginalPhotoFilename = (filename: string): boolean => /^.+\.jpg$/.test(filename) && !SCALED_NAME_RE.test(filename);

const ensureDirectory = async (FileSystem: FileSystemInterface, path: string) => {
  if (!(await FileSystem.getDirectoryExists(path))) {
    await FileSystem.makeDirectoryAsync(path, { intermediates: true });
  }
};

interface Indexes {
  scanIdByPhotoId: Map<string, string>;
  photoIdsByScanId: Map<string, string[]>;
}

/**
 * The doc-dir half of the photo gallery: photos that have been captured but not
 * yet uploaded. Lives at `${documentDirectory}photos/<scanId>/<photoId>.jpg`
 * and is **not** LRU-managed — entries leave only by upload (`markPhotoSynced`),
 * explicit delete (`deletePhoto`), or emergency erase.
 *
 * Maintains two in-memory indexes (scanId-by-photoId, photoIds-by-scanId) built
 * lazily by scanning the doc-dir. Keeps both indexes consistent on every
 * mutation so callers never have to.
 *
 * **Thread safety:** mutations are not internally serialized. The owning gallery
 * uses a shared mutex to interleave index updates with FileCache operations
 * atomically (e.g. `markPhotoSynced` = move to cache + remove from index must
 * happen as one unit). Callers must serialize concurrent mutators themselves.
 */
export class UnsyncedPhotoIndex {
  private readonly fileSystem: FileSystemInterface;
  private readonly unsyncedPhotosDir: string;
  private indexes: Promise<Indexes>;

  constructor({ fileSystem, documentDirectory }: { fileSystem: FileSystemInterface; documentDirectory: string }) {
    this.fileSystem = fileSystem;
    this.unsyncedPhotosDir = `${documentDirectory}photos/`;
    this.indexes = this.build();
  }

  // ********************************************
  // Pure path helpers
  // ********************************************

  /** Returns the scan's unsynced-photos directory path (trailing slash). */
  getScanDir(scanId: string): string {
    return `${this.unsyncedPhotosDir}${scanId}/`;
  }

  /** Returns the absolute path where a photo would live. Does NOT check existence. */
  getPhotoPath(scanId: string, photoId: string): string {
    return pathJoin(this.getScanDir(scanId), `${photoId}.jpg`);
  }

  // ********************************************
  // Filesystem mutations
  // ********************************************

  /** Ensure both the unsynced-photos dir AND the per-scan dir exist. */
  async ensureScanDir(scanId: string): Promise<void> {
    await ensureDirectory(this.fileSystem, this.unsyncedPhotosDir);
    await ensureDirectory(this.fileSystem, this.getScanDir(scanId));
  }

  /** Delete a scan dir if present. Used when known empty or for emergency erase. */
  async deleteScanDir(scanId: string): Promise<void> {
    const path = this.getScanDir(scanId);
    if (await this.fileSystem.getDirectoryExists(path)) {
      await this.fileSystem.deleteDirectoryAsync(path);
    }
  }

  // ********************************************
  // Index mutations — O(1) on the in-memory maps
  // ********************************************

  /** Register a photo as unsynced. Caller must have already written the file at {@link getPhotoPath}. */
  async add(photoId: string, scanId: string): Promise<void> {
    const indexes = await this.indexes;
    indexes.scanIdByPhotoId.set(photoId, scanId);
    const photoIds = indexes.photoIdsByScanId.get(scanId);
    if (photoIds) photoIds.push(photoId);
    else indexes.photoIdsByScanId.set(scanId, [photoId]);
  }

  /** De-register a photo. Caller is responsible for deleting the file. Idempotent — unknown photoId is a no-op. */
  async remove(photoId: string): Promise<void> {
    const indexes = await this.indexes;
    const scanId = indexes.scanIdByPhotoId.get(photoId);
    if (!scanId) return;
    indexes.scanIdByPhotoId.delete(photoId);
    const photoIds = indexes.photoIdsByScanId.get(scanId)?.filter(id => id !== photoId) ?? [];
    if (photoIds.length === 0) indexes.photoIdsByScanId.delete(scanId);
    else indexes.photoIdsByScanId.set(scanId, photoIds);
  }

  /** Re-scan disk. Call after operations that bypass add/remove (e.g. emergency erase). */
  async rebuild(): Promise<void> {
    this.indexes = this.build();
    await this.indexes;
  }

  // ********************************************
  // Queries — return snapshot copies, callers may mutate freely
  // ********************************************

  /** Path to the photo file if currently unsynced; undefined otherwise. Does NOT touch the filesystem. */
  async getPathForPhoto(photoId: string): Promise<string | undefined> {
    const scanId = (await this.indexes).scanIdByPhotoId.get(photoId);
    return scanId && this.getPhotoPath(scanId, photoId);
  }

  /** True iff the photo is in the index. Does NOT touch the filesystem. */
  async has(photoId: string): Promise<boolean> {
    return (await this.indexes).scanIdByPhotoId.has(photoId);
  }

  /** Snapshot of scanId-by-photoId. */
  async getScanIdsByPhotoId(): Promise<Map<string, string>> {
    return new Map((await this.indexes).scanIdByPhotoId);
  }

  /** Snapshot of photoIds-by-scanId. Inner arrays are also copied. */
  async getPhotoIdsByScanId(): Promise<Map<string, string[]>> {
    const indexes = await this.indexes;
    const out = new Map<string, string[]>();
    for (const [scanId, photoIds] of indexes.photoIdsByScanId) out.set(scanId, [...photoIds]);
    return out;
  }

  /** Unique scan IDs that have at least one indexed unsynced photo. */
  async getScanIds(): Promise<string[]> {
    return [...(await this.indexes).photoIdsByScanId.keys()];
  }

  /** Snapshot of photo IDs for one scan. Empty array if none. */
  async getPhotoIdsForScan(scanId: string): Promise<string[]> {
    return [...((await this.indexes).photoIdsByScanId.get(scanId) ?? [])];
  }

  // ********************************************
  // Aggregate operations
  // ********************************************

  /**
   * Cumulative bytes + photo count. Walks the doc-dir on every call (no caching) —
   * stats reflect actual on-disk state, so any external mutations show up immediately.
   */
  async getStats(): Promise<PhotoSetStats> {
    if (!(await this.fileSystem.getDirectoryExists(this.unsyncedPhotosDir))) {
      return { numBytes: 0, numPhotos: 0 };
    }
    const [numBytes, { directories }] = await Promise.all([
      this.fileSystem.getDirectorySize(this.unsyncedPhotosDir),
      this.fileSystem.getDirectoryContents(this.unsyncedPhotosDir),
    ]);
    let numPhotos = 0;
    for (const dir of directories) {
      const { files } = await this.fileSystem.getDirectoryContents(`${this.unsyncedPhotosDir}${dir}`);
      numPhotos += files.length;
    }
    return { numBytes, numPhotos };
  }

  // ********************************************
  // Internal — directory scan & index build
  // ********************************************

  /** Scan IDs that have a directory on disk under the unsynced root (regardless of index state). */
  async getScanDirsOnDisk(): Promise<string[]> {
    if (!(await this.fileSystem.getDirectoryExists(this.unsyncedPhotosDir))) return [];
    const { directories } = await this.fileSystem.getDirectoryContents(this.unsyncedPhotosDir);
    const scanIds: string[] = [];
    for (const dir of directories) {
      const scanId = peek(dir.split('/'));
      if (scanId) scanIds.push(scanId);
    }
    return scanIds;
  }

  /** Absolute file paths in a scan's unsynced dir. Empty if the dir doesn't exist. */
  async getFilesInScanDir(scanId: string): Promise<string[]> {
    const scanDir = this.getScanDir(scanId);
    if (!(await this.fileSystem.getDirectoryExists(scanDir))) return [];
    const { files } = await this.fileSystem.getDirectoryContents(scanDir);
    return files.map(file => `${scanDir}${file}`);
  }

  private async build(): Promise<Indexes> {
    const scanIdByPhotoId = new Map<string, string>();
    const photoIdsByScanId = new Map<string, string[]>();
    for (const scanId of await this.getScanDirsOnDisk()) {
      const files = await this.getFilesInScanDir(scanId);
      if (files.length === 0) {
        // Best-effort cleanup of empty scan dirs encountered during the scan; no data loss.
        await safeFsOp(`UnsyncedPhotoIndex.build: delete empty scan dir ${scanId}`, () => this.deleteScanDir(scanId));
        continue;
      }
      const photoIds: string[] = [];
      let foundOriginal = false;
      for (const filePath of files) {
        // Only originals belong in the unsynced index — scaled-named files are legacy artifacts.
        if (!isOriginalPhotoFilename(filePath)) continue;
        const photoId = extractPhotoIdFromFilename(filePath);
        scanIdByPhotoId.set(photoId, scanId);
        photoIds.push(photoId);
        foundOriginal = true;
      }
      if (foundOriginal) photoIdsByScanId.set(scanId, photoIds);
    }
    return { scanIdByPhotoId, photoIdsByScanId };
  }
}
